import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, BookOpen, FileText, Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ApiError, friendlyMessage } from '@/api/http'
import { genreTemplateApi } from '@/api/genreTemplateApi'
import { novelImportApi, type NovelImportConfirmInput, type NovelImportPreviewInput } from '@/api/novelImportApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScreenplayImportPreviewPanel } from '@/components/ingest/ScreenplayImportPreview'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { resolveLocalized } from '@/i18n/resolveLocalized'
import { CONTENT_LANGUAGES, defaultContentLanguage } from '@/lib/contentLanguages'
import {
  NOVEL_IMPORT_MAX_FILE_BYTES,
  NOVEL_IMPORT_MAX_RUNES,
  NOVEL_IMPORT_WARNING_RUNES,
  countNovelRunes,
  decodeNovelFile,
} from './novelImportDecode'
import { isNovelImportPreviewCurrent, novelImportPreviewKey } from './novelImportPreviewState'
import type { NovelImportPreview } from '@/types/nf'

export const NOVEL_IMPORT_ACCEPT = '.txt,.md,.fdx,.fountain,.spmd'

export function novelImportStrategyLabel(strategy: string, t: TFunction): string {
  return strategy === 'builtin'
    ? t('novelImport.strategyBuiltin')
    : t('novelImport.strategyCustom')
}

function normalizeOptional(value: string) {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function novelImportErrorMessage(error: unknown, t: (key: string) => string) {
  if (error instanceof ApiError) {
    if (error.status === 413) return t('novelImport.errors.tooLarge')
    if (error.status === 400 && (error.code === 'no_chapters' || error.message.includes('no_chapters'))) {
      return t('novelImport.errors.noChapters')
    }
  }
  return friendlyMessage(error, t('novelImport.errors.generic'))
}

export default function NovelImportPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [title, setTitle] = useState('')
  const [genre, setGenre] = useState('')
  const [contentLanguage, setContentLanguage] = useState(() => defaultContentLanguage(i18n.resolvedLanguage ?? i18n.language))
  const [text, setText] = useState('')
  const [customRegex, setCustomRegex] = useState('')
  const [allowMarkdownHeading, setAllowMarkdownHeading] = useState(false)
  const [format, setFormat] = useState<'fdx' | 'fountain' | undefined>(undefined)
  const [fileName, setFileName] = useState('')
  const [fileEncoding, setFileEncoding] = useState('')
  const [previewResult, setPreviewResult] = useState<{ key: string; preview: NovelImportPreview } | null>(null)

  const templatesQuery = useQuery({
    queryKey: ['genre-templates'],
    queryFn: genreTemplateApi.list,
  })

  const lang = i18n.resolvedLanguage ?? i18n.language
  const genreOptions = useMemo(
    () =>
      (templatesQuery.data ?? []).map((template) => ({
        key: template.genre_key,
        label: resolveLocalized(template.payload.genre_label, lang) || template.genre_key,
      })),
    [lang, templatesQuery.data],
  )

  const previewBody = useMemo<NovelImportPreviewInput>(
    () => ({
      text,
      format,
      custom_regex: normalizeOptional(customRegex),
      allow_markdown_heading: allowMarkdownHeading,
    }),
    [allowMarkdownHeading, customRegex, format, text],
  )
  const currentPreviewKey = novelImportPreviewKey(previewBody)
  const currentPreviewKeyRef = useRef(currentPreviewKey)
  currentPreviewKeyRef.current = currentPreviewKey

  const previewMutation = useMutation({
    mutationFn: (body: NovelImportPreviewInput) => novelImportApi.preview(body),
    onSuccess: (result, variables) => {
      const key = novelImportPreviewKey(variables)
      if (key === currentPreviewKeyRef.current) setPreviewResult({ key, preview: result })
    },
    onError: (error) => {
      setPreviewResult(null)
      toast.error(novelImportErrorMessage(error, t))
    },
  })

  const confirmMutation = useMutation({
    mutationFn: (body: NovelImportConfirmInput) => novelImportApi.confirm(body),
    onSuccess: (result) => {
      toast.success(t('novelImport.imported', { count: result.chapter_count }))
      navigate(`/projects/${result.project_id}`)
    },
    onError: (error) => toast.error(novelImportErrorMessage(error, t)),
  })

  const hasText = text.trim().length > 0
  const hasTitle = title.trim().length > 0
  const runeCount = useMemo(() => countNovelRunes(text), [text])
  const isNearRuneLimit = runeCount >= NOVEL_IMPORT_WARNING_RUNES && runeCount <= NOVEL_IMPORT_MAX_RUNES
  const isOverRuneLimit = runeCount > NOVEL_IMPORT_MAX_RUNES
  const preview =
    previewResult && isNovelImportPreviewCurrent(previewResult.key, previewBody) ? previewResult.preview : null
  const canConfirm = Boolean(preview && preview.chapter_count >= 1 && hasTitle && hasText && !isOverRuneLimit && !confirmMutation.isPending)

  function invalidatePreview() {
    setPreviewResult(null)
    previewMutation.reset()
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > NOVEL_IMPORT_MAX_FILE_BYTES) {
      event.target.value = ''
      setFileName('')
      setFileEncoding('')
      toast.error(t('novelImport.errors.fileTooLarge'))
      return
    }
    setFileName(file.name)
    setFileEncoding('')
    const lowerName = file.name.toLowerCase()
    setFormat(lowerName.endsWith('.fdx') ? 'fdx' : lowerName.endsWith('.fountain') || lowerName.endsWith('.spmd') ? 'fountain' : undefined)
    if (lowerName.endsWith('.md')) setAllowMarkdownHeading(true)
    invalidatePreview()
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (!(result instanceof ArrayBuffer)) {
        toast.error(t('novelImport.errors.readFile'))
        return
      }
      const decoded = decodeNovelFile(result)
      setText(decoded.text)
      setFileEncoding(decoded.encoding)
      if (decoded.encoding === 'gb18030') toast.info(t('novelImport.encodingConverted'))
      invalidatePreview()
    }
    reader.onerror = () => toast.error(t('novelImport.errors.readFile'))
    reader.readAsArrayBuffer(file)
  }

  function handlePreview() {
    if (!hasText) {
      toast.error(t('novelImport.errors.emptyText'))
      return
    }
    if (isOverRuneLimit) {
      toast.error(t('novelImport.errors.textTooLarge'))
      return
    }
    setPreviewResult(null)
    previewMutation.mutate(previewBody)
  }

  function handleConfirm() {
    if (!hasTitle) {
      toast.error(t('project.titleRequired'))
      return
    }
    if (!preview || preview.chapter_count < 1) {
      toast.error(t('novelImport.errors.noChapters'))
      return
    }
    confirmMutation.mutate({
      ...previewBody,
      title: title.trim(),
      genre: normalizeOptional(genre),
      content_language: normalizeOptional(contentLanguage),
    })
  }

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-2 flex items-center gap-2 text-sm text-primary">
            <BookOpen className="size-4" />
            {t('novelImport.kicker')}
          </p>
          <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{t('novelImport.title')}</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{t('novelImport.subtitle')}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/projects')}>
          {t('project.backToProjects')}
        </Button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="space-y-5">
          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">{t('novelImport.sourceTitle')}</CardTitle>
              <CardDescription>{t('novelImport.sourceDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input ref={fileInputRef} type="file" accept={NOVEL_IMPORT_ACCEPT} className="hidden" onChange={handleFileChange} />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="size-4" />
                  {t('novelImport.upload')}
                </Button>
                <div className="min-w-0 text-sm text-muted-foreground">
                  {fileName ? (
                    <span className="inline-flex max-w-full items-center gap-2 truncate">
                      <FileText className="size-4 shrink-0" />
                      <span className="truncate">{fileName}</span>
                      {fileEncoding ? <Badge variant="outline">{t('novelImport.encodingLabel', { encoding: fileEncoding })}</Badge> : null}
                    </span>
                  ) : (
                    t('novelImport.uploadHint')
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('novelImport.limitHint')}</p>

              <div className="space-y-2">
                <Label htmlFor="novel-import-text">{t('novelImport.textLabel')}</Label>
                <textarea
                  id="novel-import-text"
                  value={text}
                  onChange={(event) => {
                    setText(event.target.value)
                    setFileEncoding('')
                    setFormat(undefined)
                    invalidatePreview()
                  }}
                  placeholder={t('novelImport.textPlaceholder')}
                  className="min-h-[320px] w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                />
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className={isOverRuneLimit ? 'text-destructive' : isNearRuneLimit ? 'text-amber-600 dark:text-amber-400' : undefined}>
                    {t('novelImport.textStats', { count: runeCount.toLocaleString() })}
                  </span>
                  {isOverRuneLimit ? (
                    <span className="text-destructive">{t('novelImport.overLimitHint')}</span>
                  ) : isNearRuneLimit ? (
                    <span className="text-amber-600 dark:text-amber-400">{t('novelImport.nearLimitHint')}</span>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">{t('novelImport.metaTitle')}</CardTitle>
              <CardDescription>{t('novelImport.metaDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="novel-import-title">{t('project.name')}</Label>
                <Input
                  id="novel-import-title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder={t('novelImport.titlePlaceholder')}
                  aria-invalid={!hasTitle && title.length > 0}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="novel-import-genre">{t('project.genre')}</Label>
                <Input
                  id="novel-import-genre"
                  value={genre}
                  onChange={(event) => setGenre(event.target.value)}
                  placeholder={t('novelImport.genrePlaceholder')}
                  list="novel-import-genre-options"
                />
                <datalist id="novel-import-genre-options">
                  {genreOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>{t('project.wizard.contentLanguage')}</Label>
                <Select value={contentLanguage} onValueChange={setContentLanguage}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_LANGUAGES.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.native}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">{t('novelImport.advancedTitle')}</CardTitle>
              <CardDescription>{t('novelImport.advancedDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="novel-import-regex">{t('novelImport.customRegex')}</Label>
                <Input
                  id="novel-import-regex"
                  value={customRegex}
                  onChange={(event) => {
                    setCustomRegex(event.target.value)
                    invalidatePreview()
                  }}
                  placeholder={t('novelImport.customRegexPlaceholder')}
                />
              </div>
              <label className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={allowMarkdownHeading}
                  onChange={(event) => {
                    setAllowMarkdownHeading(event.target.checked)
                    invalidatePreview()
                  }}
                />
                <span>{t('novelImport.allowMarkdownHeading')}</span>
              </label>
              <div className="flex flex-wrap gap-3">
                <Button type="button" onClick={handlePreview} disabled={!hasText || isOverRuneLimit || previewMutation.isPending}>
                  {previewMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t('novelImport.preview')}
                </Button>
                <Button type="button" variant="secondary" onClick={handleConfirm} disabled={!canConfirm}>
                  {confirmMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t('novelImport.confirm')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="rounded-lg">
            <CardHeader>
              <CardTitle className="text-lg">{t('novelImport.previewTitle')}</CardTitle>
              <CardDescription>{t('novelImport.previewDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              {previewMutation.isPending ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-48" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : preview ? (
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{t('novelImport.chapterCount', { count: preview.chapter_count })}</Badge>
                    <Badge variant="outline">{t('novelImport.strategy', { value: novelImportStrategyLabel(preview.strategy, t) })}</Badge>
                  </div>
                  {preview.regex ? (
                    <details className="rounded-lg border bg-muted/20 p-3 text-sm">
                      <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                        {t('novelImport.advancedPreview')}
                      </summary>
                      <p className="mt-2 break-all font-mono text-xs text-muted-foreground">
                        {t('novelImport.regex', { value: preview.regex })}
                      </p>
                    </details>
                  ) : null}

                  {(preview.warnings ?? []).length ? (
                    <div className="space-y-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/35 dark:text-amber-200">
                      {(preview.warnings ?? []).map((warning, index) => (
                        <p key={`${warning}-${index}`} className="flex gap-2">
                          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                          <span>{warning}</span>
                        </p>
                      ))}
                    </div>
                  ) : null}

                  {preview.screenplay ? (
                    <ScreenplayImportPreviewPanel screenplay={preview.screenplay} />
                  ) : (
                    <div className="space-y-3">
                      {(preview.chapters ?? []).map((chapter) => (
                        <details key={`${chapter.index}-${chapter.title}`} className="rounded-lg border bg-card/60 p-4" open={chapter.index <= 3}>
                          <summary className="cursor-pointer list-none">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <p className="text-sm font-medium">
                                  {t('novelImport.chapterLine', { index: chapter.index, title: chapter.title })}
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {t('novelImport.wordCount', { count: chapter.word_count })}
                                </p>
                              </div>
                              <span className="text-xs text-muted-foreground">{t('novelImport.expandHint')}</span>
                            </div>
                          </summary>
                          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{chapter.content_preview}</p>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid min-h-[360px] place-items-center rounded-lg border border-dashed bg-card/45 p-8 text-center">
                  <div>
                    <p className="text-lg font-medium">{t('novelImport.emptyPreview')}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{t('novelImport.emptyPreviewHint')}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
